import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext.jsx';
import { api } from '@/api/client';
import { Settings, Gift, ShoppingCart, FileText, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function Home() {
  const nav = useNavigate();
  const { total } = useCart();
  const { toast } = useToast();
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Check if rewards system is enabled
  const [rewardsEnabled, setRewardsEnabled] = useState(false);
  
  // Loading states for smooth animations
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [rewardsLoaded, setRewardsLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const checkConnection = async () => {
    try {
      const { data } = await api.get('/auth/check-connection');
      setConnectionStatus(data);
      return data.connected;
    } catch (error) {
      console.error('Connection check failed:', error);
      setConnectionStatus({ connected: false, error: 'Connection check failed' });
      return false;
    }
  };

  const fetchBranding = async () => {
    try {
      const { data } = await api.get('/live/branding', { params: { live: 1 } });
      setBranding(data);
      setBrandingLoaded(true);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      setBrandingLoaded(true); // Still mark as loaded even on error
    }
  };

  const fetchRecentInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/live/invoices', { 
        params: { live: 1, limit: 10 } 
      });
      setRecentInvoices(data.invoices || data || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast({
        title: 'Error',
        description: 'Could not load recent invoices',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRewardsSettings = async () => {
    try {
      const { data } = await api.get('/settings/rewards');
      console.log('Fetched rewards settings from API:', data);
      setRewardsEnabled(data.enabled || false);
      setRewardsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch rewards settings:', error);
      // Fallback to localStorage for backward compatibility
      const s = localStorage.getItem('rewards_settings');
      if (s) {
        const settings = JSON.parse(s);
        console.log('Using localStorage rewards settings:', settings);
        setRewardsEnabled(settings.enabled || false);
      } else {
        setRewardsEnabled(false);
      }
      setRewardsLoaded(true);
    }
  };

  // Auto-load branding and invoices on dashboard mount
  useEffect(() => {
    const initialize = async () => {
      const isConnected = await checkConnection();
      if (isConnected) {
        // Load branding and rewards in parallel
        await Promise.all([
          fetchBranding(),
          fetchRewardsSettings()
        ]);
        
        // Short delay before marking initialization complete for smooth animation
        setTimeout(() => {
          setIsInitializing(false);
          fetchRecentInvoices();
        }, 300);
      } else {
        setIsInitializing(false);
      }
    };
    initialize();
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Connection Status Banner */}
      {connectionStatus && !connectionStatus.connected && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-red-900">CRM Connection Lost</h3>
              <p className="text-sm text-red-700 mt-1">
                Your connection to your CRM has expired. Please reconnect to continue using the POS.
              </p>
            </div>
            <Button 
              onClick={() => nav('/settings')}
              className="bg-red-600 hover:bg-red-700"
            >
              Restore Connection
            </Button>
          </div>
        </div>
      )}

      {/* Branding Section with Skeleton Loader */}
      <div className="flex items-center gap-3 mb-6 min-h-[40px]">
        {!brandingLoaded ? (
          // Skeleton loader for branding
          <div className="flex items-center gap-3 animate-pulse">
            <div className="h-10 w-10 bg-gray-200 rounded"></div>
            <div className="h-8 w-48 bg-gray-200 rounded"></div>
          </div>
        ) : (
          // Actual branding with fade-in animation
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-500">
            {branding?.logo_url && (
              <img 
                src={branding.logo_url} 
                alt="Logo" 
                className="h-10 w-auto object-contain"
              />
            )}
            {branding?.business_name && (
              <h1 className="text-2xl font-semibold tracking-tight">{branding.business_name}</h1>
            )}
            {!branding?.business_name && !branding?.logo_url && (
              <h1 className="text-2xl font-semibold tracking-tight">Castle POS</h1>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons with Skeleton Loaders */}
      {!rewardsLoaded ? (
        // Skeleton loader for buttons
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 animate-pulse">
          <div className="h-24 bg-gray-200 rounded-lg"></div>
          <div className="h-24 bg-gray-200 rounded-lg"></div>
          <div className="h-24 bg-gray-200 rounded-lg"></div>
        </div>
      ) : (
        // Actual buttons with staggered fade-in
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Button 
            data-testid="new-sale-button" 
            className="h-24 text-lg animate-in fade-in slide-in-from-left-4 duration-500" 
            onClick={() => nav('/sale')}
            style={{ animationDelay: '100ms' }}
          >
            <ShoppingCart className="mr-2"/> New Sale
          </Button>
          {rewardsEnabled && (
            <Button 
              data-testid="lookup-rewards-button" 
              variant="secondary" 
              className="h-24 text-lg animate-in fade-in slide-in-from-left-4 duration-500" 
              onClick={() => nav('/rewards')}
              style={{ animationDelay: '200ms' }}
            >
              <Gift className="mr-2"/> Rewards
            </Button>
          )}
          <Button 
            data-testid="settings-button" 
            variant="outline" 
            className="h-24 text-lg animate-in fade-in slide-in-from-left-4 duration-500" 
            onClick={() => nav('/settings')}
            style={{ animationDelay: rewardsEnabled ? '300ms' : '200ms' }}
          >
            <Settings className="mr-2"/> Settings
          </Button>
        </div>
      )}

      {/* Recent Transactions Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Recent Transactions
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchRecentInvoices}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent className="max-h-[500px] overflow-y-auto">
          {recentInvoices.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No transactions yet. Click "Refresh" to load recent transactions.</p>
            </div>
          )}
          
          {recentInvoices.length > 0 && (
            <div className="space-y-2">
              {recentInvoices.map((invoice) => (
                <div
                  key={invoice._id || invoice.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => nav(`/invoice-payment/${invoice._id || invoice.id}`, { state: { invoice } })}
                >
                  <div className="flex-1">
                    <div className="font-semibold">
                      Transaction #{invoice.invoiceNumber || invoice._id}
                    </div>
                    <div className="text-sm text-gray-600">
                      {invoice.contactDetails?.name || 'Walk-in Customer'}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" />
                      {new Date(invoice.createdAt || invoice.issueDate).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">
                      ${(invoice.total || invoice.amountDue || 0).toFixed(2)}
                    </div>
                    <div className={`text-sm font-medium ${
                      invoice.status === 'paid' ? 'text-green-600' : 
                      invoice.status === 'draft' ? 'text-gray-500' : 
                      'text-orange-600'
                    }`}>
                      {invoice.status?.toUpperCase() || 'DRAFT'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
